import React, {FC, useEffect, useMemo, useRef, useState} from "react";
import {
  FlightDrawerDetailsHeader,
  FlightDrawerDetailsTitle,
  FlightDrawerDetailsTitleWrapper,
  FlightDrawerDetailsWrapper,
} from "./FlightDrawerDetailsStyledComponents";
import {FlyRoute} from "../../flyRoute/FlyRoute";
import {defineMessages, useIntl} from "react-intl";
import {
  IFreighterOrderRequest,
  IOrderRequestPlane
} from "../../../../store/reducers/freighterRequestsReducer/freighterRequestsReducerTypes";
import {OrderTypeShield} from "../../orderType/OrderTypeShield";
import {API} from "../../../../API/API";
import {AirportApiGetAirportRequest} from "../../../../proto/generated/api_entities_pb";
import {IAirport} from "../../../../store/reducers/fleetReducer/fleetReducerTypes";
import {mapTimeZoneEntityToITimezone} from "../../../../store/reducers/fleetReducer/fleetMappers";
import {useAppDispatch, useAppSelector} from "../../../../store/store";
import {createEvent, clearCreateEvent} from "../../../../store/reducers/scheduleReducer/scheduleReducer";
import {createEmptyLeg, clearCreateEmptyLeg} from "../../../../store/reducers/emptyLegsReducer/emptyLegsReducer";
import {ScheduleType} from "../../../../proto/generated/api_entities_pb";
import {Dropdown, Tooltip, message} from "antd";
import CalendarIcon from "../../../../media/icons/calendar.svg?react";
import styled from "styled-components";
import {DistanceUnit} from "../../../../utils/constans/statuses/distanceUnits";
import {convertToKm} from "../../../../utils/helpers/distanceConverter/distanceConverter";


const FlightDrawerOrderRequestOrderDetailsMessages = defineMessages({
  title: {
    id: "flightDrawerOrderRequestOrderDetails.title",
    defaultMessage: "Route details",
  },
  quickAddToSchedule: {
    id: "orderRequestCardMessages.quickAddToSchedule",
    defaultMessage: "Quick add to schedule",
  },
  addToSchedulePublic: {
    id: "orderRequestCardMessages.addToSchedulePublic",
    defaultMessage: "Add to schedule (Public)",
  },
  addToSchedulePrivate: {
    id: "orderRequestCardMessages.addToSchedulePrivate",
    defaultMessage: "Add to schedule (Private)",
  },
})

const QuickAddButtonWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 12px;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 6px;
  background-color: #1890ff;
  transition: background-color 0.2s, opacity 0.2s;

  &:hover {
    background-color: #40a9ff;
  }

  &:active {
    opacity: 0.9;
  }

  svg {
    width: 20px;
    height: 20px;
  }

  svg,
  svg path {
    fill: #fff !important;
  }

  &:hover svg,
  &:hover svg path {
    fill: #fff !important;
  }
`;

interface IFlightDrawerOrderRequestOrderDetails {
  orderRequest: IFreighterOrderRequest;
  approvedPlane?: IOrderRequestPlane | null;
}

export const FlightDrawerOrderRequestOrderDetails: FC<IFlightDrawerOrderRequestOrderDetails> = ({
                                                                                                  orderRequest,
                                                                                                  approvedPlane,
                                                                                                }) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const {orderRouteOrderRequests} = orderRequest;
  const [fetchedPositioningAirport, setFetchedPositioningAirport] = useState<IAirport | null>(null);
  /** True after we've attempted to fetch the positioning airport (when not in routes). Used to avoid logging "not found" while fetch is still in progress. */
  const [positioningAirportFetchComplete, setPositioningAirportFetchComplete] = useState(true);
  const userDistanceUnit = useAppSelector(state => state.user.oneItemObject.item.distanceUnit) || DistanceUnit.NAUTICAL_MILE;
  const userCompany = useAppSelector(state => state.user.oneItemObject.item.company);
  
  // Get create states for schedule and empty legs
  const {
    isLoading: isScheduleCreating,
    isSuccess: isScheduleCreateSuccess,
    errorMessage: scheduleCreateErrorMessage,
  } = useAppSelector(state => state.schedule.create);
  
  const {
    isLoading: isEmptyLegCreating,
    isSuccess: isEmptyLegCreateSuccess,
    errorMessage: emptyLegCreateErrorMessage,
  } = useAppSelector(state => state.emptyLegs.create);
  
  // Refs to track if we've already processed the current state to prevent duplicate messages
  const prevScheduleStateRef = useRef<{isLoading: boolean, isSuccess: boolean, errorMessage?: string}>({
    isLoading: false,
    isSuccess: false,
  });
  const prevEmptyLegStateRef = useRef<{isLoading: boolean, isSuccess: boolean, errorMessage?: string}>({
    isLoading: false,
    isSuccess: false,
  });
  const scheduleMessageShownRef = useRef<string | null>(null);
  const emptyLegMessageShownRef = useRef<string | null>(null);
  const isPositioningRequestInProgressRef = useRef<boolean>(false);
  const isRouteRequestInProgressRef = useRef<boolean>(false);
  const lastProcessedScheduleStateRef = useRef<string | null>(null);
  const lastProcessedEmptyLegStateRef = useRef<string | null>(null);
  // Track last dispatched requests to prevent exact duplicates
  const lastDispatchedRouteRequestRef = useRef<{routeIndex: number, isPublic: boolean, timestamp: number} | null>(null);
  const lastDispatchedPositioningRequestRef = useRef<{isPublic: boolean, timestamp: number} | null>(null);
  
  // Get plane and fleet info from approvedPlane
  const planeId = approvedPlane?.planeId;
  // Note: In createEmptyLeg, the "fleetId" parameter is actually the planeId (see emptyLegsSaga.ts line 107)
  // So we just use planeId directly
  
  // Handler for adding positioning flight as empty leg (always creates public event)
  const handleAddPositioningToEmptyLeg = () => {
    // Prevent duplicate dispatches if already creating or request in progress
    if (isEmptyLegCreating || isPositioningRequestInProgressRef.current) {
      return;
    }
    
    // Check if we just dispatched the exact same request (within 2 seconds)
    const now = Date.now();
    const lastRequest = lastDispatchedPositioningRequestRef.current;
    if (lastRequest && (now - lastRequest.timestamp) < 2000) {
      return; // Prevent duplicate dispatch
    }
    
    if (!positioningRoute || !planeId) {
      message.error("Missing required data for positioning flight");
      return;
    }
    
    // Note: In createEmptyLeg, "fleetId" parameter is actually the planeId (see emptyLegsSaga.ts)
    // So we use planeId directly
    
    const {airportFrom, airportTo, dateFrom, flyHoursDuration, flyMinutesDuration, distance} = positioningRoute;
    
    if (!dateFrom) {
      message.error("Missing departure date for positioning flight");
      return;
    }
    
    // Use positioning departure time and calculate arrival as departure + flight time
    const positioningDepartureTime = dateFrom; // This is positioningDepartureDate from backend
    const positioningFlightDurationMinutes = (flyHoursDuration * 60) + flyMinutesDuration;
    const positioningArrivalTime = new Date(positioningDepartureTime.getTime() + positioningFlightDurationMinutes * 60000);
    
    // Convert distance to km if needed
    const distanceKm = convertToKm(distance, userDistanceUnit);
    
    // Double-check we're not already creating (race condition protection)
    if (isEmptyLegCreating || isPositioningRequestInProgressRef.current) {
      return;
    }
    
    // Mark as in progress immediately before dispatch
    isPositioningRequestInProgressRef.current = true;
    // Reset processed state ref for new request
    lastProcessedEmptyLegStateRef.current = null;
    // Track this request to prevent duplicates (always public, so no need to track isPublic)
    lastDispatchedPositioningRequestRef.current = { isPublic: true, timestamp: Date.now() };
    
    // For positioning flights, we use specific departure date (not available date range)
    // Setting both departureDate and availableFromDate/availableToDate causes MULTIPLE_TIME_SETUP error
    dispatch(createEmptyLeg({
      departureDate: positioningDepartureTime, // Positioning departure time from backend
      availableFromDate: undefined, // Don't set - would conflict with departureDate
      availableToDate: undefined, // Don't set - would conflict with departureDate
      airportFromId: airportFrom.id,
      airportToId: airportTo.id,
      fleetId: planeId, // Note: This is actually planeId, not fleetId (see emptyLegsSaga.ts)
      priceAmount: 0,
      priceCurrencyId: 0,
      hours: flyHoursDuration,
      minutes: flyMinutesDuration,
      distance: distanceKm,
      comment: `Positioning flight from order request #${orderRequest.id} (arrival: ${positioningArrivalTime.toISOString()})`,
      priceOnRequest: true, // Positioning flights typically don't have a fixed price
    }));
  };
  
  // Handler for adding regular route to schedule
  const handleAddRouteToSchedule = (routeIndex: number, isPublic: boolean) => {
    // Prevent duplicate dispatches if already creating or request in progress
    if (isScheduleCreating || isRouteRequestInProgressRef.current) {
      return;
    }
    
    // Check if we just dispatched the exact same request (within 2 seconds)
    const now = Date.now();
    const lastRequest = lastDispatchedRouteRequestRef.current;
    if (lastRequest && 
        lastRequest.routeIndex === routeIndex && 
        lastRequest.isPublic === isPublic && 
        (now - lastRequest.timestamp) < 2000) {
      return; // Prevent duplicate dispatch
    }
    
    if (!approvedPlane || !planeId) {
      message.error("Missing approved plane information");
      return;
    }
    
    const orderRouteOrderRequest = orderRouteOrderRequests[routeIndex];
    if (!orderRouteOrderRequest) {
      message.error("Route not found");
      return;
    }
    
    const {orderRoute, flyHours, flyMinutes} = orderRouteOrderRequest;
    const departureDate = orderRoute.departureDate;
    
    if (!departureDate) {
      message.error("Missing departure date");
      return;
    }
    
    // Calculate arrival date
    const flyDurationMinutes = (flyHours || 0) * 60 + (flyMinutes || 0);
    const arrivalDate = new Date(departureDate.getTime() + flyDurationMinutes * 60000);
    
    // Get refueling count from approvedPlane or route
    const refuelingCount = approvedPlane.refuelingCount ?? orderRouteOrderRequest.technicalStopCount;
    
    // Create schedule name
    const airportFromCode = orderRoute.airportFrom.iataCode || orderRoute.airportFrom.icaoCode || '';
    const airportToCode = orderRoute.airportTo.iataCode || orderRoute.airportTo.icaoCode || '';
    const scheduleName = airportFromCode && airportToCode 
      ? `${airportFromCode} - ${airportToCode}`
      : `Route ${routeIndex + 1} - Order #${orderRequest.id}`;
    
    // Double-check we're not already creating (race condition protection)
    if (isScheduleCreating || isRouteRequestInProgressRef.current) {
      return;
    }
    
    // Mark as in progress immediately before dispatch
    isRouteRequestInProgressRef.current = true;
    // Reset processed state ref for new request
    lastProcessedScheduleStateRef.current = null;
    // Track this request to prevent duplicates
    lastDispatchedRouteRequestRef.current = { routeIndex, isPublic, timestamp: Date.now() };
    
    dispatch(createEvent({
      eventType: ScheduleType.ORDER_REQUEST,
      eventName: scheduleName,
      planeId: planeId,
      dateFrom: departureDate,
      dateTo: arrivalDate,
      airportFromId: orderRoute.airportFrom.id,
      airportToId: orderRoute.airportTo.id,
      refuelingCount: refuelingCount && refuelingCount > 0 ? refuelingCount : undefined,
      pax: orderRoute.pax && orderRoute.pax > 0 ? orderRoute.pax : undefined,
      isPublic: isPublic,
      orderRequestId: orderRequest.id,
    }));
  };

  // Check if we have positioning flight data - only show if positioning is included (has duration and distance)
  const positioningRoute = useMemo(() => {
    if (!approvedPlane) {
      return null;
    }

    const positioningFromAirportId = approvedPlane.positioningFromAirportId;
    const positioningDurationMinutes = approvedPlane.positioningDurationMinutes;
    const positioningDistance = approvedPlane.positioningDistanceKm;
    const firstRouteOrderRequest = orderRouteOrderRequests[0];

    // Only show positioning route if positioning is included (has duration > 0 and distance > 0)
    // Need positioningFromAirportId, positioningDuration, positioningDistance, departureDate, and first route to create positioning route
    if (!positioningFromAirportId || 
        !positioningDurationMinutes || 
        positioningDurationMinutes <= 0 ||
        !positioningDistance || 
        positioningDistance <= 0 ||
        !firstRouteOrderRequest) {
      return null;
    }
    
    // Use positioningDepartureDate from approvedPlane if available (exact datetime from backend calculations)
    // Otherwise fall back to calculating from departureDate
    let positioningDeparture: Date;
    let positioningArrival: Date;
    
    if (approvedPlane.positioningDepartureDate) {
      // Use the exact positioning departure date from backend
      positioningDeparture = new Date(approvedPlane.positioningDepartureDate);
      if (isNaN(positioningDeparture.getTime())) {
        return null;
      }
      
      // Positioning arrival equals first leg departure (departureDate)
      const departureDateStr = approvedPlane.departureDate;
      if (departureDateStr) {
        positioningArrival = new Date(departureDateStr);
        if (isNaN(positioningArrival.getTime())) {
          return null;
        }
      } else {
        // Fallback to first route departure date
        const firstRouteDeparture = firstRouteOrderRequest.orderRoute.departureDate;
        if (!firstRouteDeparture) return null;
        positioningArrival = firstRouteDeparture;
      }
    } else {
      // Fallback: calculate from departureDate if positioningDepartureDate not available
      const departureDateStr = approvedPlane.departureDate;
      if (!departureDateStr) {
        // Fallback to first route departure date if departureDate is not available
        const firstRouteDeparture = firstRouteOrderRequest.orderRoute.departureDate;
        if (!firstRouteDeparture) return null;
        
        // Calculate positioning departure by subtracting positioning duration
        positioningDeparture = new Date(firstRouteDeparture.getTime() - positioningDurationMinutes * 60000);
        positioningArrival = firstRouteDeparture;
      
      } else {
        // Parse departureDate string to Date
        const departureDate = new Date(departureDateStr);
        if (isNaN(departureDate.getTime())) {
          return null;
        }
        
        // Positioning arrival is the departureDate (when first leg starts)
        positioningArrival = departureDate;
        // Positioning departure is before arrival by positioning duration
        positioningDeparture = new Date(positioningArrival.getTime() - positioningDurationMinutes * 60000);
      }
    }
    
    // Try to find the positioning departure airport from order routes
    // NOTE: Backend should include positioningFromAirport (full AirportEntity) in OrderRequestPlaneEntity
    // to avoid this lookup. For now, we try to find it in the order routes.
    let positioningFromAirport = orderRouteOrderRequests.find(
      r => r.orderRoute.airportFrom.id === positioningFromAirportId
    )?.orderRoute.airportFrom;
    
    // If not found in routes, try airportTo
    if (!positioningFromAirport) {
      positioningFromAirport = orderRouteOrderRequests.find(
        r => r.orderRoute.airportTo.id === positioningFromAirportId
      )?.orderRoute.airportTo;
    }
    
    // If not found in routes, use fetched airport if available
    if (!positioningFromAirport && fetchedPositioningAirport && fetchedPositioningAirport.id === positioningFromAirportId) {
      positioningFromAirport = fetchedPositioningAirport;
    }
    
    if (!positioningFromAirport) {
      return null;
    }
    
    // Convert positioning duration to hours and minutes
    const positioningHours = Math.floor(positioningDurationMinutes / 60);
    const positioningMinutes = positioningDurationMinutes % 60;
    
    return {
      airportFrom: positioningFromAirport,
      airportTo: firstRouteOrderRequest.orderRoute.airportFrom,
      dateFrom: positioningDeparture,
      dateTo: positioningArrival,
      flyHoursDuration: positioningHours,
      flyMinutesDuration: positioningMinutes,
      distance: positioningDistance || 0,
      pax: 0, // Positioning flights typically have no passengers
    };
  }, [approvedPlane, orderRouteOrderRequests, fetchedPositioningAirport, positioningAirportFetchComplete]);

  // Fetch positioning airport if not found in routes
  useEffect(() => {
    if (!approvedPlane?.positioningFromAirportId) {
      setFetchedPositioningAirport(null);
      setPositioningAirportFetchComplete(true);
      return;
    }

    const positioningFromAirportId = approvedPlane.positioningFromAirportId;

    // Check if airport is already in routes
    const airportInRoutes = orderRouteOrderRequests.find(
      r => r.orderRoute.airportFrom.id === positioningFromAirportId ||
           r.orderRoute.airportTo.id === positioningFromAirportId
    );

    if (airportInRoutes) {
      setFetchedPositioningAirport(null);
      setPositioningAirportFetchComplete(true);
      return;
    }

    // Not in routes: fetch from API; mark incomplete until fetch settles
    setPositioningAirportFetchComplete(false);

    const fetchAirport = async () => {
      try {
        const req = new AirportApiGetAirportRequest().setAirportid(positioningFromAirportId);
        const airportEntity = (await API.getAirportById(req)).getAirport();
        if (airportEntity) {
          const timezoneEntity = airportEntity.getTimezone();
          const airport: IAirport = {
            id: airportEntity.getAirportid(),
            name: airportEntity.getName(),
            icaoCode: airportEntity.getIcaocode(),
            iataCode: airportEntity.getIatacode(),
            countryName: airportEntity.getCountryname(),
            timezone: timezoneEntity ? mapTimeZoneEntityToITimezone(timezoneEntity) : undefined,
          };
          setFetchedPositioningAirport(airport);
        } else {
          setFetchedPositioningAirport(null);
        }
      } catch {
        setFetchedPositioningAirport(null);
      } finally {
        setPositioningAirportFetchComplete(true);
      }
    };

    fetchAirport();
  }, [approvedPlane?.positioningFromAirportId, orderRouteOrderRequests]);
  
  // Handle schedule creation success/error
  useEffect(() => {
    // Only process when transitioning from loading to not loading
    const wasLoading = prevScheduleStateRef.current.isLoading;
    const isNowLoading = isScheduleCreating;
    
    // Only process if we just finished (was loading, now not loading)
    if (!wasLoading || isNowLoading) {
      // Update ref to track current state
      prevScheduleStateRef.current = {
        isLoading: isScheduleCreating,
        isSuccess: isScheduleCreateSuccess,
        errorMessage: scheduleCreateErrorMessage,
      };
      return;
    }
    
    // Create a unique state signature for this result
    const stateSignature = `${isScheduleCreateSuccess ? 'success' : 'error'}-${scheduleCreateErrorMessage || ''}`;
    
    // Only process if this is a different state than we last processed
    // This prevents duplicate messages even if useEffect runs multiple times
    if (lastProcessedScheduleStateRef.current === stateSignature) {
      // Already processed this state, just update tracking
      prevScheduleStateRef.current = {
        isLoading: isScheduleCreating,
        isSuccess: isScheduleCreateSuccess,
        errorMessage: scheduleCreateErrorMessage,
      };
      isRouteRequestInProgressRef.current = false;
      return;
    }
    
    // Mark this state as processed IMMEDIATELY to prevent race conditions
    lastProcessedScheduleStateRef.current = stateSignature;
    
    // Show message only once
    if (isScheduleCreateSuccess) {
      message.success("Route added to schedule");
      dispatch(clearCreateEvent());
    } else if (scheduleCreateErrorMessage) {
      message.error(scheduleCreateErrorMessage);
      dispatch(clearCreateEvent());
    }
    
    // Reset request in progress flag
    isRouteRequestInProgressRef.current = false;
    
    // Clear last dispatched request ref when request completes (success or error)
    if (!isNowLoading && wasLoading) {
      lastDispatchedRouteRequestRef.current = null;
    }
    
    // Update ref to track current state
    prevScheduleStateRef.current = {
      isLoading: isScheduleCreating,
      isSuccess: isScheduleCreateSuccess,
      errorMessage: scheduleCreateErrorMessage,
    };
  }, [isScheduleCreating, isScheduleCreateSuccess, scheduleCreateErrorMessage, dispatch]);
  
  // Handle empty leg creation success/error
  useEffect(() => {
    // Only process when transitioning from loading to not loading
    const wasLoading = prevEmptyLegStateRef.current.isLoading;
    const isNowLoading = isEmptyLegCreating;
    
    // Only process if we just finished (was loading, now not loading)
    if (!wasLoading || isNowLoading) {
      // Update ref to track current state
      prevEmptyLegStateRef.current = {
        isLoading: isEmptyLegCreating,
        isSuccess: isEmptyLegCreateSuccess,
        errorMessage: emptyLegCreateErrorMessage,
      };
      return;
    }
    
    // Create a unique state signature for this result
    const stateSignature = `${isEmptyLegCreateSuccess ? 'success' : 'error'}-${emptyLegCreateErrorMessage || ''}`;
    
    // Only process if this is a different state than we last processed
    // This prevents duplicate messages even if useEffect runs multiple times
    if (lastProcessedEmptyLegStateRef.current === stateSignature) {
      // Already processed this state, just update tracking
      prevEmptyLegStateRef.current = {
        isLoading: isEmptyLegCreating,
        isSuccess: isEmptyLegCreateSuccess,
        errorMessage: emptyLegCreateErrorMessage,
      };
      isPositioningRequestInProgressRef.current = false;
      return;
    }
    
    // Mark this state as processed IMMEDIATELY to prevent race conditions
    lastProcessedEmptyLegStateRef.current = stateSignature;
    
    // Show message only once
    if (isEmptyLegCreateSuccess) {
      message.success("Positioning flight added to empty legs");
      dispatch(clearCreateEmptyLeg());
    } else if (emptyLegCreateErrorMessage) {
      message.error(emptyLegCreateErrorMessage);
      dispatch(clearCreateEmptyLeg());
    }
    
    // Reset request in progress flag
    isPositioningRequestInProgressRef.current = false;
    
    // Clear last dispatched request ref when request completes (success or error)
    if (!isNowLoading && wasLoading) {
      lastDispatchedPositioningRequestRef.current = null;
    }
    
    // Update ref to track current state
    prevEmptyLegStateRef.current = {
      isLoading: isEmptyLegCreating,
      isSuccess: isEmptyLegCreateSuccess,
      errorMessage: emptyLegCreateErrorMessage,
    };
  }, [isEmptyLegCreating, isEmptyLegCreateSuccess, emptyLegCreateErrorMessage, dispatch]);

  // Combine positioning route (if exists) with regular routes
  const allRoutes = useMemo(() => {
    const routes: Array<{route: typeof positioningRoute | typeof orderRouteOrderRequests[0], isPositioning: boolean, index: number}> = [];
    
    // Add positioning route first if it exists
    if (positioningRoute) {
      routes.push({route: positioningRoute, isPositioning: true, index: -1});
    }
    
    // Add regular routes
    orderRouteOrderRequests.forEach((orderRouteOrderRequest, index) => {
      routes.push({route: orderRouteOrderRequest, isPositioning: false, index});
    });
    
    return routes;
  }, [positioningRoute, orderRouteOrderRequests]);
  
  // Create quick add button for positioning route (empty leg) - always creates public event
  const createPositioningButton = () => {
    // Show button if we have positioning route and approved plane with planeId
    if (!approvedPlane || !planeId || !positioningRoute || orderRequest.isArchived) {
      return null;
    }
    
    return (
      <Tooltip title={intl.formatMessage(FlightDrawerOrderRequestOrderDetailsMessages.quickAddToSchedule)}>
        <QuickAddButtonWrapper 
          onClick={(e) => {
            e.stopPropagation();
            // Prevent multiple clicks if already processing
            if (isEmptyLegCreating || isPositioningRequestInProgressRef.current) {
              return;
            }
            handleAddPositioningToEmptyLeg();
          }}
        >
          <CalendarIcon/>
        </QuickAddButtonWrapper>
      </Tooltip>
    );
  };
  
  // Create quick add button for regular route (schedule)
  const createRouteButton = (routeIndex: number) => {
    if (!approvedPlane || !planeId || orderRequest.isArchived) {
      return null;
    }
    
    const routeMenu = {
      onClick: ({key}: { key: string }) => {
        // Prevent multiple clicks if already processing
        if (isScheduleCreating || isRouteRequestInProgressRef.current) {
          return;
        }
        if (key === 'public') {
          handleAddRouteToSchedule(routeIndex, true);
        } else if (key === 'private') {
          handleAddRouteToSchedule(routeIndex, false);
        }
      },
      items: [
        { key: 'private', label: intl.formatMessage(FlightDrawerOrderRequestOrderDetailsMessages.addToSchedulePrivate) },
        { key: 'public', label: intl.formatMessage(FlightDrawerOrderRequestOrderDetailsMessages.addToSchedulePublic) },
      ],
    };
    
    return (
      <Dropdown 
        menu={routeMenu}
        trigger={['click']}
        placement="bottomRight"
      >
        <Tooltip title={intl.formatMessage(FlightDrawerOrderRequestOrderDetailsMessages.quickAddToSchedule)}>
          <QuickAddButtonWrapper onClick={(e) => e.stopPropagation()}>
            <CalendarIcon/>
          </QuickAddButtonWrapper>
        </Tooltip>
      </Dropdown>
    );
  };

  return (
    <FlightDrawerDetailsWrapper>
      <FlightDrawerDetailsHeader>
        <FlightDrawerDetailsTitleWrapper>
          <FlightDrawerDetailsTitle>{intl.formatMessage(FlightDrawerOrderRequestOrderDetailsMessages.title)}</FlightDrawerDetailsTitle>
        </FlightDrawerDetailsTitleWrapper>
        <OrderTypeShield type={orderRequest.order.type} styleType={'fill'}/>
      </FlightDrawerDetailsHeader>

      {allRoutes.map(({route, isPositioning, index}, routeIndex) => {
        if (isPositioning && positioningRoute) {
          // Render positioning route
          return (
            <FlyRoute 
              key={`positioning-${routeIndex}`}
              index={0}
              airportFrom={positioningRoute.airportFrom}
              airportTo={positioningRoute.airportTo}
              dateFrom={positioningRoute.dateFrom}
              dateTo={positioningRoute.dateTo}
              flyHoursDuration={positioningRoute.flyHoursDuration}
              flyMinutesDuration={positioningRoute.flyMinutesDuration}
              distance={positioningRoute.distance}
              pax={positioningRoute.pax}
              isPositioning={true}
              titleAction={createPositioningButton()}
            />
          );
        } else if (!isPositioning) {
          // Render regular route - adjust index to account for positioning route
          const orderRouteOrderRequest = route as typeof orderRouteOrderRequests[0];
          const {flyHours, flyMinutes, flyDistance, technicalStopDuration = 0, orderRoute} = orderRouteOrderRequest;
          
          // Use departure date from orderRouteOrderRequest.orderRoute (orderrequestsList.orderrouteorderrequestsList.orderroute.departuredate)
          // This has the correct actual departure date
          const departureDate = orderRoute.departureDate;
          
          if ([flyHours, flyMinutes, flyDistance].some(p => p === undefined) || !departureDate) {
            return null;
          }

          // Calculate flyDuration in minutes from flyHours and flyMinutes
          const flyDurationMinutes = (flyHours! * 60) + flyMinutes!;
          
          // Calculate arrival date from departure + flyDuration
          const calculatedArrivalDate = new Date(departureDate.getTime() + flyDurationMinutes * 60000);

          // Calculate route index: if positioning exists, regular routes should display as Route 1, 2, etc.
          // Since FlyRoute adds 1 to the index, we need to subtract 1 if positioning exists
          const routeIndexForDisplay = positioningRoute ? routeIndex - 1 : routeIndex;

          return (
            <FlyRoute 
              key={orderRouteOrderRequest.orderRouteOrderRequestId}
              index={routeIndexForDisplay}
              airportFrom={orderRoute.airportFrom}
              airportTo={orderRoute.airportTo}
              dateFrom={departureDate}
              dateTo={calculatedArrivalDate}
              flyHoursDuration={flyHours!}
              flyMinutesDuration={flyMinutes!}
              distance={flyDistance!}
              technicalStopHoursDuration={Math.floor(technicalStopDuration / 60)}
              technicalStopMinutesDuration={technicalStopDuration % 60}
              pax={orderRoute.pax}
              isPositioning={false}
              titleAction={createRouteButton(index)}
            />
          );
        }
        return null;
      })}
    </FlightDrawerDetailsWrapper>
  );
};